import { Cartesian2, Cartesian3 } from "cesium";
import { EmbankmentType, } from "../../types";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { getEarcutTriangulation } from "../../utilities/extra-utils";
import { checkPositionWrtToTileset } from "../../utilities/local-utilities";
import { PlaneSlopeStrategy } from "../side-slope/plane-slope-strategy";
import { SideSlopeGeometry } from "../side-slope/side-slope-geometry";
import { getSideSlopeChunk } from "../side-slope/utils";
export class FlatGroundGeometry {
    _id;
    _controlPoints;
    _sideSlopes;
    _slopeStrategy;
    _geometry;
    get geometry() {
        return this._geometry;
    }
    constructor(id) {
        this._id = id;
        this._controlPoints = [];
        this._geometry = { indices: [], vertices: [] };
        // this.centerGeometries = [];
        // this.embankmentGeometries = [];
        // this.cutGeometries = [];
        this._sideSlopes = [];
        this._slopeStrategy = new PlaneSlopeStrategy();
        // this._nonIndexedGeometry = { vertices: [], indices: [] };
    }
    dispose() {
        // this._nonIndexedGeometry.vertices.length = 0;
        // this._nonIndexedGeometry.indices.length = 0;
        // if (this._controlPointsForFlatGround) {
        //     this._controlPointsForFlatGround.length = 0;
        // }
    }
    computeInterpolatedPointsOnPerimeter(points, interpolationPitch) {
        const result = [];
        for (let i = 0; i < points.length; i++) {
            const currPoint = points[i];
            const nextPoint = points[i + 1] !== undefined ? points[i + 1] : points[0];
            const interpolatedPoints = EarthBrainUtils.getLinearInterpolation(currPoint, nextPoint, interpolationPitch, false, false, true);
            const endPoint = interpolatedPoints[interpolatedPoints.length - 1];
            if (endPoint) {
                const distanceBtwLast2Points = Cartesian3.distance(endPoint, nextPoint);
                if (distanceBtwLast2Points - interpolationPitch < -0.001) {
                    interpolatedPoints.pop();
                }
            }
            result.push(currPoint, ...interpolatedPoints);
        }
        return result;
    }
    getSideSlopeChunk(points, settings, tileset) {
        const upDir = Cartesian3.UNIT_Z;
        // Determine winding order using all points for accurate determination
        const isAntiClockwise = EarthBrainUtils.isPolygonCounterClockwise(points);
        const perimeterPoints = this.computeInterpolatedPointsOnPerimeter(points, settings.commonSettings.interpolationPitch);
        const perimeterPointInfos = [];
        for (let i = 0; i < perimeterPoints.length; ++i) {
            if (perimeterPointInfos.length > 0) {
                if (Cartesian3.equals(perimeterPoints[i], perimeterPointInfos[perimeterPointInfos.length - 1].position)) {
                    continue;
                }
            }
            const nextPoint = perimeterPoints[(i + 1) % perimeterPoints.length];
            const prevPoint = perimeterPoints[(perimeterPoints.length + i - 1) % perimeterPoints.length];
            const incomingDir = EarthBrainUtils.getDirectionOfPointOnSitePath(prevPoint, perimeterPoints[i], upDir);
            const outgoingDir = EarthBrainUtils.getDirectionOfPointOnSitePath(perimeterPoints[i], nextPoint, upDir);
            const dir = EarthBrainUtils.addSiteDirection(incomingDir, outgoingDir);
            const outwardDirection = isAntiClockwise ? dir.right : dir.left;
            perimeterPointInfos.push({
                position: perimeterPoints[i],
                outwardDirection,
            });
        }
        const getReferencePosition = (pos) => {
            return checkPositionWrtToTileset(pos, tileset);
        };
        return getSideSlopeChunk(perimeterPointInfos, getReferencePosition, true);
    }
    resetGeometry() {
        // this.centerGeometries = [];
        this._sideSlopes = [];
        // this.cutGeometries = [];
    }
    updateCentralGeometry(points) {
        this.resetGeometry();
        this._controlPoints = points;
        this.createCenterGeometry();
    }
    updateGeometry(points, settings, tileset) {
        // removing all primitives from viewer and removing all geometries
        if (points.length < 2)
            return;
        this.resetGeometry();
        this._controlPoints = points;
        if (settings.commonSettings.withSlope !== false && tileset) {
            const sideSlopePoints = this.getSideSlopeChunk(this._controlPoints, settings, tileset);
            const isCLoseLoop = sideSlopePoints.length <= 1;
            for (const chunck of sideSlopePoints) {
                const intersectionStrategy = (ray) => {
                    const position = tileset.rayCast(ray);
                    return position;
                };
                let sideSlopeGeometry;
                if (chunck.isPointAbove) {
                    sideSlopeGeometry = new SideSlopeGeometry({
                        pointInfos: chunck.pointInfos,
                        verticalDirection: new Cartesian3(0, 0, -1),
                        closeLoop: isCLoseLoop,
                        generateShelf: settings.embankmentSettings.generateShelf,
                        intersectionStrategy,
                        settings: {
                            moundSlope: settings.embankmentSettings.moundSlope,
                            shelfSlope: settings.embankmentSettings.shelfSlope,
                            shelfWidth: settings.embankmentSettings.shelfWidth,
                            slopeHeight: settings.embankmentSettings.height,
                        },
                    });
                }
                else {
                    sideSlopeGeometry = new SideSlopeGeometry({
                        pointInfos: chunck.pointInfos,
                        verticalDirection: new Cartesian3(0, 0, 1),
                        closeLoop: isCLoseLoop,
                        generateShelf: settings.cutSettings.generateShelf,
                        intersectionStrategy,
                        settings: {
                            moundSlope: settings.cutSettings.moundSlope,
                            shelfSlope: settings.cutSettings.shelfSlope,
                            shelfWidth: settings.cutSettings.shelfWidth,
                            slopeHeight: settings.cutSettings.height,
                        },
                    });
                }
                sideSlopeGeometry.constructSlope(settings.commonSettings.interpolationPitch, this._slopeStrategy);
                sideSlopeGeometry.buildGeometry();
                this._sideSlopes.push({
                    geometry: sideSlopeGeometry,
                    isFill: chunck.isPointAbove,
                });
            }
            // connect chuncks
            for (let i = 0; i < this._sideSlopes.length; ++i) {
                const nextIndex = (i + 1) % this._sideSlopes.length;
                if (i === nextIndex)
                    break;
                const firstPoint = this._sideSlopes[i].geometry.pointInfos[this._sideSlopes[i].geometry.pointInfos.length - 1].position;
                const nextPoint = this._sideSlopes[nextIndex].geometry.pointInfos[0].position;
                const connectingPoint = new Cartesian3();
                Cartesian3.multiplyByScalar(Cartesian3.add(firstPoint, nextPoint, connectingPoint), 0.5, connectingPoint);
                this._sideSlopes[i].geometry.addConnectingPoint(connectingPoint, false);
                this._sideSlopes[nextIndex].geometry.addConnectingPoint(connectingPoint, true);
            }
        }
    }
    /**
     * Function will create array center geometries from flatground control points.
     */
    createCenterGeometry() {
        const tempCenterGeometry = { indices: [], vertices: [] };
        // Need to be x,y pairs
        const earcutPoints = this._controlPoints.map((c) => new Cartesian2(c.x, c.y));
        //vertices
        const { faces } = getEarcutTriangulation(earcutPoints);
        tempCenterGeometry.vertices.push(...Cartesian3.packArray(this._controlPoints));
        //indices
        tempCenterGeometry.indices.push(...faces);
        const centreGeometry = {
            geometry: tempCenterGeometry,
            nonIndexedGeometry: { vertices: [], indices: [] },
            segmentType: EmbankmentType.NONE,
        };
        let count = 0;
        for (const index of tempCenterGeometry.indices) {
            centreGeometry.nonIndexedGeometry.vertices.push(centreGeometry.geometry.vertices[index * 3], centreGeometry.geometry.vertices[index * 3 + 1], centreGeometry.geometry.vertices[index * 3 + 2]);
            centreGeometry.nonIndexedGeometry.indices.push(count);
            count++;
        }
        return centreGeometry;
    }
    getSubGeometries() {
        // return [...this.centerGeometries, ...this.embankmentGeometries, ...this.cutGeometries];
        const result = [];
        const cutGeometry = {
            indices: [],
            vertices: [],
        };
        const fillGeometry = {
            indices: [],
            vertices: [],
        };
        const fills = this._sideSlopes.filter((s) => s.isFill);
        const cuts = this._sideSlopes.filter((s) => !s.isFill);
        for (const slope of fills) {
            EarthBrainUtils.mergeIGeom(fillGeometry, slope.geometry.geometry);
        }
        for (const slope of cuts) {
            EarthBrainUtils.mergeIGeom(cutGeometry, slope.geometry.geometry);
        }
        if (cutGeometry.indices.length > 0) {
            result.push({
                segmentType: EmbankmentType.CUT,
                geometry: cutGeometry,
                nonIndexedGeometry: cutGeometry,
            });
        }
        if (fillGeometry.indices.length > 0) {
            result.push({
                segmentType: EmbankmentType.FILL,
                geometry: fillGeometry,
                nonIndexedGeometry: fillGeometry,
            });
        }
        const centerGeometry = this.createCenterGeometry();
        result.push(centerGeometry);
        this._geometry.indices.length = 0;
        this._geometry.vertices.length = 0;
        EarthBrainUtils.mergeIGeom(this._geometry, centerGeometry.geometry);
        EarthBrainUtils.mergeIGeom(this._geometry, cutGeometry);
        EarthBrainUtils.mergeIGeom(this._geometry, fillGeometry);
        return result;
    }
    computeSurfaceArea(flatgroundCenterGeom) {
        let totalArea = 0;
        const indices = flatgroundCenterGeom.indices;
        try {
            const vertices = Cartesian3.unpackArray(flatgroundCenterGeom.vertices);
            for (let i = 0; i < indices.length; i += 3) {
                const indexA = indices[i];
                const indexB = indices[i + 1];
                const indexC = indices[i + 2];
                const A = vertices[indexA];
                const B = vertices[indexB];
                const C = vertices[indexC];
                const result = EarthBrainUtils.calculateTriangleArea(A, B, C);
                totalArea += result;
            }
        }
        catch (error) {
            console.error(error);
        }
        return totalArea;
    }
}
//# sourceMappingURL=flatground-geometry.js.map